import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { CFG } from '../core/config.js';

/**
 * Lighting rig: cascaded shadow maps, a sky-driven key/fill/ambient set and a
 * small IBL probe so metal reads as metal.
 *
 * ── Cascades ──────────────────────────────────────────────────────────────
 * Each cascade is a directional light with its own shadow map. Cascade 0 wears
 * the visible sun colour; the rest are colourless and exist purely to carry a
 * second and third shadow map, which the world shaders fold in through
 * `getShadowMask()` (a product, i.e. the union of the cascades).
 *
 * Two things make that union behave:
 *  • every cascade is fitted to the bounding sphere of its own slice of the
 *    view frustum, and its centre is snapped to the shadow map's own texel
 *    grid, so the map does not crawl when the camera moves — the sphere fit
 *    also makes the extent rotation-invariant, so it does not pulse when the
 *    camera turns;
 *  • the outer cascades only receive *large* casters (walls, towers, trees).
 *    Small dynamic things would otherwise stamp a coarse, fattened copy of
 *    their near-cascade shadow onto the same ground. Three renders cascade 0
 *    (and 1) itself; the outer maps are rendered by `beforeRender()` with the
 *    small casters temporarily switched off.
 *
 * Cascade blending is done in the world shaders' distance fade (see
 * `setShadowBox` in world/mat.js) plus a per-cascade filter radius that grows
 * with texel size, so the transition never shows a hard line.
 *
 * Publishes `world.lighting`.
 */

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

/* module-scope scratch — nothing in the update path allocates */
const _up = new THREE.Vector3(0, 1, 0);
const _ax = new THREE.Vector3();
const _ay = new THREE.Vector3();
const _az = new THREE.Vector3();
const _c = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _col = new THREE.Color();
const _col2 = new THREE.Color();
const _dir = new THREE.Vector3();
const _smallCasters = [];
let _smallCasterWas = new Uint8Array(32);

/**
 * Mood table.
 *
 * `fog` is the *post* aerial-perspective density in 1/m, `fogH` its height
 * falloff in 1/m about `fogRef` metres, `fogMax` the blend ceiling (so the
 * far ridge never loses its silhouette completely) and `fogDesat` the share
 * of chroma distance removes on top of the tint. `sceneFog` is the much
 * weaker `FogExp2` the world shaders apply; it exists so alpha-blended and
 * additive geometry still recedes, and is deliberately small because
 * `FogExp2` has no ceiling and would erase the horizon.
 *
 * `key` is the one number that decides whether the image has a highlight at
 * all. A low sun puts N·L around 0.45 on flat ground, and after albedo/π that
 * turns even a generous irradiance into a mid-tone — which is exactly how a
 * golden-hour field ends up as grey mud with nothing above sRGB 156 and an
 * ACES shoulder that never gets used. The key is therefore set from the
 * *output* it has to produce (sunlit ground at sRGB ~200, armour specular
 * clipping), not from a plausible-looking irradiance.
 *
 * `skyWarm` is how much of the sun-side horizon bleeds into the hemisphere's
 * sky colour, and `ambCool` how far the flat ambient is pulled towards
 * `fillCol`. Together they are the warm-key / cool-shadow separation: with
 * both at 0 the ambient inherits the sun's own amber and every shadow reads
 * neutral grey, which is the classic "no colour discipline" tell.
 */
export const MOODS = {
  golden:      { sky: 'golden',   key: 20.0, fill: 0.34, hemi: 0.44, amb: 0.34,
                 fog: 0.0042, fogH: 0.0022, fogRef: 3.0, fogMax: 0.90, fogDesat: 0.60, sceneFog: 0.00046,
                 grade: 'battle', fillCol: 0x7ea2dc, exposure: 1.0,
                 skyWarm: 0.04, ambCool: 0.75, bounceCool: 0.24, shadow: 0.66 },
  overcast:    { sky: 'overcast', key: 4.2, fill: 0.62, hemi: 1.05, amb: 0.85,
                 fog: 0.0062, fogH: 0.0026, fogRef: 3.0, fogMax: 0.94, fogDesat: 0.34, sceneFog: 0.00060,
                 grade: 'neutral', fillCol: 0x9fb0c4, exposure: 1.10,
                 skyWarm: 0.22, ambCool: 0.15, bounceCool: 0.0, shadow: 0.34 },
  storm:       { sky: 'storm',    key: 2.6, fill: 0.56, hemi: 0.86, amb: 0.70,
                 fog: 0.0084, fogH: 0.0024, fogRef: 3.0, fogMax: 0.96, fogDesat: 0.38, sceneFog: 0.00075,
                 grade: 'storm', fillCol: 0x7d90ad, exposure: 1.16,
                 skyWarm: 0.24, ambCool: 0.20, bounceCool: 0.0, shadow: 0.40 },
  dusk:        { sky: 'dusk',     key: 15.5, fill: 0.32, hemi: 0.48, amb: 0.36,
                 fog: 0.0050, fogH: 0.0024, fogRef: 3.0, fogMax: 0.90, fogDesat: 0.58, sceneFog: 0.00050,
                 grade: 'dusk', fillCol: 0x5f7db8, exposure: 1.06,
                 skyWarm: 0.10, ambCool: 0.55, bounceCool: 0.18, shadow: 0.64 },
  'night-fire':{ sky: 'night',    key: 0.75, fill: 0.30, hemi: 0.40, amb: 0.38,
                 fog: 0.0052, fogH: 0.0030, fogRef: 3.0, fogMax: 0.92, fogDesat: 0.26, sceneFog: 0.00055,
                 grade: 'night', fillCol: 0xff8a44, exposure: 1.30,
                 skyWarm: 0.30, ambCool: 0.0, bounceCool: 0.0, shadow: 0.48 },
};

export function createLighting() {
  const N = clamp(CFG.q.cascades | 0, 1, 4);
  const MAPSIZE = CFG.q.shadowMap;

  const sun = new THREE.DirectionalLight(0xffe6c0, 4.2);
  const fill = new THREE.DirectionalLight(0x86a8d8, 0.55);
  const amb = new THREE.HemisphereLight(0x9fb6d8, 0x4a3c28, 0.7);
  const ambient = new THREE.AmbientLight(0x2a3242, 0.55);

  const cascades = [];   // { light, target, radius, split, texel }
  let mood = 'golden';
  let pmrem = null, envRT = null, envTex = null;
  let scanTick = 0;

  /** Practical (logarithmic ⇄ uniform blend) split scheme. */
  function computeSplits(near, far, n, lambda, out) {
    out[0] = near;
    for (let i = 1; i < n; i++) {
      const p = i / n;
      const log = near * Math.pow(far / near, p);
      const uni = near + (far - near) * p;
      out[i] = lambda * log + (1 - lambda) * uni;
    }
    out[n] = far;
    return out;
  }

  const SHADOW_NEAR = 0.6;
  const SHADOW_FAR = 196;
  const splits = new Float32Array(5);

  /** Minimal bounding sphere radius of the frustum slice [n, f]. */
  function sliceSphere(cam, n, f, out) {
    const ty = Math.tan((cam.fov * Math.PI) / 360);
    const tx = ty * cam.aspect;
    const k2 = tx * tx + ty * ty;
    let zc, r;
    if (k2 * k2 * (f - n) * (f - n) >= (f * f - n * n) * (f * f - n * n) || k2 >= (f - n) / (f + n)) {
      zc = f; r = f * Math.sqrt(k2);
    } else {
      zc = 0.5 * (f + n) * (1 + k2);
      r = 0.5 * Math.sqrt((f - n) * (f - n) + 2 * (f * f + n * n) * k2 + (f + n) * (f + n) * k2 * k2);
    }
    out.x = zc; out.y = r;
    return out;
  }
  const _slice = { x: 0, y: 0 };

  function makeShadowMap(light, size) {
    // Pre-creating the map keeps three from calling updateProjectionMatrix()
    // on the shadow camera, which would clobber the cascade matrix we author
    // by hand every frame.
    const rt = new THREE.WebGLRenderTarget(size, size, {
      minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
      format: THREE.RGBAFormat, type: THREE.UnsignedByteType,
      depthBuffer: true, stencilBuffer: false, generateMipmaps: false,
    });
    rt.texture.name = light.name + '.shadowMap';
    light.shadow.map = rt;
  }

  function buildCascades(w) {
    computeSplits(SHADOW_NEAR, SHADOW_FAR, N, 0.74, splits);
    for (let i = 0; i < N; i++) {
      const light = i === 0 ? sun : new THREE.DirectionalLight(0x000000, 0);
      light.name = 'csm' + i;
      light.castShadow = true;
      const target = new THREE.Object3D();
      light.target = target;
      const sh = light.shadow;
      sh.mapSize.set(MAPSIZE, MAPSIZE);
      sh.autoUpdate = true;
      sh.camera.near = 1;
      sh.camera.far = 800;
      makeShadowMap(light, MAPSIZE);
      cascades.push({ light, target, radius: 1, texel: 1, index: i });
      w.scene.add(light, target);
    }
    // Every shadow-casting directional light must precede the non-casting
    // ones: three indexes `directionalLightShadows[i]` with the *light* index.
    w.scene.add(fill, amb, ambient);
  }

  /** Fit one cascade to its frustum slice and snap it to the texel grid. */
  function fitCascade(cs, cam, dirX, dirY, dirZ, n, f) {
    // At night the sun is under the terrain; keep the shadow projector above
    // the horizon or every cascade renders the world lit from below.
    if (dirY < 0.12) {
      const s = Math.hypot(dirX, dirZ) || 1;
      const k = Math.sqrt(Math.max(0, 1 - 0.12 * 0.12)) / s;
      dirX *= k; dirZ *= k; dirY = 0.12;
    }
    sliceSphere(cam, n, f, _slice);
    const R = _slice.y;
    cs.radius = R;

    // light-space basis (matches Object3D.lookAt with up = +Y)
    _az.set(dirX, dirY, dirZ).normalize();
    _ax.crossVectors(_up, _az);
    if (_ax.lengthSq() < 1e-6) _ax.set(1, 0, 0);
    _ax.normalize();
    _ay.crossVectors(_az, _ax).normalize();

    cam.getWorldDirection(_fwd);
    _c.copy(cam.position).addScaledVector(_fwd, _slice.x);

    const texel = (2 * R) / MAPSIZE;
    cs.texel = texel;
    const px = _c.dot(_ax), py = _c.dot(_ay), pz = _c.dot(_az);
    const sx = Math.round(px / texel) * texel;
    const sy = Math.round(py / texel) * texel;
    _c.set(0, 0, 0).addScaledVector(_ax, sx).addScaledVector(_ay, sy).addScaledVector(_az, pz);

    const back = 240;                        // metres of caster space above the slice
    cs.target.position.copy(_c);
    cs.target.updateMatrixWorld();
    cs.light.position.copy(_c).addScaledVector(_az, R + back);
    cs.light.updateMatrixWorld();

    const shCam = cs.light.shadow.camera;
    shCam.projectionMatrix.makeOrthographic(-R, R, R, -R, 1, back + 2 * R + 60);
    shCam.projectionMatrixInverse.copy(shCam.projectionMatrix).invert();

    // Bias scales with texel size or the outer cascades acne badly.
    cs.light.shadow.bias = -0.00016 - texel * 0.00028;
    cs.light.shadow.normalBias = Math.min(0.9, 0.55 * texel + 0.012);
    cs.light.shadow.radius = cs.index === 0 ? 1.6 : 1.0;
  }

  /** Objects small enough that only the near cascades should see them. */
  const _scanVisit = (o) => {
    if (!o.castShadow || !o.isMesh) return;
    let r = o.userData.__csmR;
    if (r === undefined) {
      const g = o.geometry;
      if (g && !g.boundingSphere) { try { g.computeBoundingSphere(); } catch (e) { /* empty geometry */ } }
      const br = g?.boundingSphere?.radius || 1;
      const sc = Math.max(Math.abs(o.scale.x), Math.abs(o.scale.y), Math.abs(o.scale.z));
      r = br * sc;
      o.userData.__csmR = r;
    }
    if (r < 3.2) _smallCasters.push(o);
  };
  function rescanSmallCasters(scene) {
    _smallCasters.length = 0;
    scene.traverse(_scanVisit);
    if (_smallCasterWas.length < _smallCasters.length) {
      let n = _smallCasterWas.length;
      while (n < _smallCasters.length) n *= 2;
      _smallCasterWas = new Uint8Array(n);
    }
  }

  const api = {
    sun, amb, ambient, fill, cascades,
    target: new THREE.Object3D(),
    mood: 'golden',
    csm: { count: N, splits, cascades },
    setMood(name) {
      const m = MOODS[name] || MOODS.golden;
      mood = MOODS[name] ? name : 'golden';
      api.mood = mood;
      api._m = m;
      api.shadowIntensity = clamp(m.shadow ?? 1, 0, 1);
      for (let i = 0; i < cascades.length; i++) cascades[i].light.shadow.intensity = api.shadowIntensity;
      api._world?.sky?.setPreset(m.sky);
      api.syncFromSky(api._world, true);
      const p = api._world?.pipeline;
      if (p) { p.grade(m.grade); p.setExposure(m.exposure); }
    },
    /** Rebuild every light colour from the current sky. Cheap; call on change. */
    syncFromSky(w, rebuildEnv) {
      const sky = w?.sky; if (!sky) return;
      const m = api._m || MOODS.golden;
      const h = Math.max(0, sky.sunDir.y);

      _col.copy(sky.sunColor);
      const mx = Math.max(_col.r, Math.max(_col.g, _col.b)) || 1;
      const lum = 0.2126 * _col.r + 0.7152 * _col.g + 0.0722 * _col.b;
      sun.color.copy(_col).multiplyScalar(1 / mx);
      sun.intensity = m.key * (0.30 + 0.70 * Math.min(1, lum * 1.6));

      // Fill: a cool sky *wrap*, not a second key. It sits high and only
      // slightly opposite the sun — pointed straight back at the sun from
      // behind the camera it front-lights every backlit figure and erases the
      // silhouette read the low key is there to create.
      _dir.set(-sky.sunDir.x * 0.45, 0.92, -sky.sunDir.z * 0.45).normalize();
      fill.position.set(_dir.x * 100, _dir.y * 100, _dir.z * 100);
      _col2.copy(sky.zenithColor);
      const mx2 = Math.max(_col2.r, Math.max(_col2.g, _col2.b)) || 1;
      fill.color.copy(_col2).multiplyScalar(1 / mx2).lerp(_col.setHex(m.fillCol).convertSRGBToLinear(), 0.45);
      fill.intensity = m.fill;

      // Hemisphere: sky above, warm sun-bounce off the grass below.
      //
      // On flat ground the hemisphere term collapses to its *sky* colour, so
      // this is what every unshadowed-but-unlit pixel in the frame is made of.
      // Letting a golden-hour horizon bleed a third of the way into it paints
      // the shadows the same amber as the key and the warm/cool separation
      // dies; `skyWarm` keeps that bleed small when the mood wants cool shade.
      _col.copy(sky.zenithColor).lerp(sky.sunHorizon, m.skyWarm ?? 0.28);
      const mxs = Math.max(_col.r, Math.max(_col.g, _col.b)) || 1;
      amb.color.copy(_col).multiplyScalar(1 / mxs);
      if (m.ambCool > 0) amb.color.lerp(_col2.setHex(m.fillCol).convertSRGBToLinear(), m.ambCool * 0.70);
      _col2.copy(sky.sunColor).multiplyScalar(0.55).addScalar(0.05);
      amb.groundColor.setRGB(
        Math.min(1, 0.30 + _col2.r * 0.55),
        Math.min(1, 0.24 + _col2.g * 0.46),
        Math.min(1, 0.16 + _col2.b * 0.34));
      if (m.bounceCool > 0) amb.groundColor.lerp(_col.setHex(m.fillCol).convertSRGBToLinear(), m.bounceCool);
      amb.intensity = m.hemi * (0.55 + 0.75 * Math.min(1, 0.25 + h));

      _col.copy(sky.ambientColor);
      const mxa = Math.max(_col.r, Math.max(_col.g, _col.b)) || 1;
      ambient.color.copy(_col).multiplyScalar(1 / Math.max(mxa, 1e-4));
      if (m.ambCool > 0) ambient.color.lerp(_col2.setHex(m.fillCol).convertSRGBToLinear(), m.ambCool);
      ambient.intensity = m.amb * Math.min(1.3, 0.25 + mxa * 2.2);

      if (w.scene.fog) {
        w.scene.fog.density = m.sceneFog;
        _col.copy(sky.horizonColor).lerp(sky.sunHorizon, 0.4);
        const mf = Math.max(_col.r, Math.max(_col.g, _col.b)) || 1;
        w.scene.fog.color.copy(_col).multiplyScalar(1 / Math.max(mf, 1e-4)).multiplyScalar(0.62 + 0.38 * mf);
      }
      if (rebuildEnv) api.rebuildEnvironment(w);
    },

    /**
     * A tiny equirectangular probe rendered straight from the analytic sky and
     * pushed through PMREM. Metals need somewhere to reflect; a flat colour
     * makes armour look like plastic.
     */
    rebuildEnvironment(w) {
      const sky = w?.sky; if (!sky || !w.renderer) return;
      const W = 64, H = 32;
      if (!envTex) {
        envTex = new THREE.DataTexture(new Float32Array(W * H * 4), W, H, THREE.RGBAFormat, THREE.FloatType);
        envTex.mapping = THREE.EquirectangularReflectionMapping;
        envTex.minFilter = THREE.LinearFilter; envTex.magFilter = THREE.LinearFilter;
        envTex.colorSpace = THREE.NoColorSpace;
      }
      const d = envTex.image.data;
      for (let j = 0; j < H; j++) {
        const theta = (j + 0.5) / H * Math.PI;      // 0 = up
        const sy = Math.cos(theta), st = Math.sin(theta);
        for (let i = 0; i < W; i++) {
          const phi = ((i + 0.5) / W) * Math.PI * 2 - Math.PI;
          const dx = st * Math.sin(phi), dz = -st * Math.cos(phi);
          sky.sample(dx, sy, dz, _col);
          const k = (j * W + i) * 4;
          if (sy < 0) {
            const g = Math.min(1, -sy * 2.4);
            d[k] = _col.r * (1 - g) + 0.045 * g;
            d[k + 1] = _col.g * (1 - g) + 0.036 * g;
            d[k + 2] = _col.b * (1 - g) + 0.026 * g;
          } else {
            d[k] = _col.r; d[k + 1] = _col.g; d[k + 2] = _col.b;
          }
          d[k + 3] = 1;
        }
      }
      envTex.needsUpdate = true;
      try {
        if (!pmrem) { pmrem = new THREE.PMREMGenerator(w.renderer); pmrem.compileEquirectangularShader(); }
        const rt = pmrem.fromEquirectangular(envTex);
        envRT?.dispose();
        envRT = rt;
        w.scene.environment = rt.texture;
        w.scene.environmentIntensity = 1.05;
      } catch (e) {
        w.scene.environment = envTex;
      }
    },

    /**
     * Render the outer cascades ourselves, with the small casters muted, right
     * before the frame's main render. Three renders the inner cascades in its
     * own pass (their `shadow.autoUpdate` stays true).
     */
    beforeRender(scene, camera) {
      const w = api._world;
      const list = api._far;
      if (!w || !list || !list.length) return;
      const renderer = w.renderer;
      scene.updateMatrixWorld();
      for (let i = 0; i < _smallCasters.length; i++) {
        const o = _smallCasters[i];
        _smallCasterWas[i] = o.castShadow ? 1 : 0;
        o.castShadow = false;
      }
      try {
        for (let i = 0; i < list.length; i++) list[i].shadow.needsUpdate = true;
        renderer.shadowMap.render(list, scene, camera);
      } catch (e) { /* never break the frame */ }
      finally {
        for (let i = 0; i < _smallCasters.length; i++) {
          _smallCasters[i].castShadow = _smallCasterWas[i] === 1;
        }
      }
    },
  };

  return {
    name: 'lighting', order: ORDER.WORLD,
    init(w) {
      api._world = w;
      api._m = MOODS.golden;
      buildCascades(w);
      // Outer cascades (index >= 2) are hand-rendered with big casters only.
      api._far = [];
      for (let i = 2; i < N; i++) {
        cascades[i].light.shadow.autoUpdate = false;
        cascades[i].light.shadow.needsUpdate = false;
        api._far.push(cascades[i].light);
      }
      w.scene.add(api.target);
      w.scene.fog = new THREE.FogExp2(0xb9c4d2, MOODS.golden.sceneFog);
      w.lighting = api;
      api.setMood('golden');
      rescanSmallCasters(w.scene);
    },

    lateUpdate(w) {
      const sky = w.sky;
      const d = sky ? sky.sunDir : _dir.set(0.4, 0.65, 0.3).normalize();
      if (sky && api._skyVersion !== sky.version) { api._skyVersion = sky.version; api.syncFromSky(w, false); }

      const cam = w.camera;
      for (let i = 0; i < N; i++) fitCascade(cascades[i], cam, d.x, d.y, d.z, splits[i], splits[i + 1]);

      // The world shaders fade their shadow term out with distance from
      // `lighting.target`, using `sun.shadow.camera.right` as the radius. That
      // has to describe the shadowed *region around the viewer* — the outer
      // cascade's own centre sits a full cascade-length down the view axis, and
      // handing them that would fade every shadow out exactly where the player
      // is standing.
      const sc = sun.shadow.camera;
      const fade = SHADOW_FAR * 1.55;   // cascade 2 reaches ~2x its radius ahead
      sc.left = -fade; sc.right = fade;
      sc.top = fade; sc.bottom = -fade;
      api.target.position.set(cam.position.x, 0, cam.position.z);
      api.target.updateMatrixWorld();

      if ((scanTick = (scanTick + 1) % 45) === 0) rescanSmallCasters(w.scene);
    },

    dispose() {
      for (const c of cascades) c.light.shadow.map?.dispose();
      pmrem?.dispose(); envRT?.dispose(); envTex?.dispose();
    },
  };
}
